import React, { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FormInputText, FormTextEditor, Stack } from '../../../base'; // Assuming paths
import { ITextActionOwnProps } from './AnswerActionComponents.interfaces';
import { useTranslation } from 'react-i18next';

interface TextActionProps extends ITextActionOwnProps {
    onSelectionChange: (value: string | undefined) => void;
    initialValue?: string;
}

interface FormValues {
    answer: string;
}

const TextAction: React.FC<TextActionProps> = ({
    isLongText,
    maxLength,
    placeholder,
    onSelectionChange,
    initialValue = '',
}) => {
    const schema = yup.object().shape({
        answer: yup
            .string()
            .max(maxLength || Infinity)
            .notRequired(),
    });
    const { t } = useTranslation();

    const methods = useForm<FormValues>({
        resolver: yupResolver(schema),
        defaultValues: { answer: initialValue },
        mode: 'onChange', // To call onSelectionChange as user types
    });

    const { watch, setValue } = methods;

    const watchedAnswer = watch('answer');

    useEffect(() => {
        onSelectionChange(watchedAnswer);
    }, [watchedAnswer, onSelectionChange]);

    // Effect to update form if initialValue changes externally after mount (optional)
    useEffect(() => {
        if (
            initialValue !== undefined &&
            initialValue !== methods.getValues('answer')
        ) {
            setValue('answer', initialValue, {
                shouldValidate: true,
                shouldDirty: true,
            });
        }
    }, [initialValue, methods, setValue]);

    return (
        <FormProvider {...methods}>
            <form
                onSubmit={(e) => e.preventDefault()}
                style={{ width: '100%', height: '100%' }}
            >
                <Stack spacing={2} sx={{ width: '100%', height: '100%' }}>
                    {isLongText ? (
                        <FormTextEditor
                            name="answer"
                            control={methods.control}
                        />
                    ) : (
                        <FormInputText
                            name="answer"
                            placeholder={
                                placeholder ||
                                t('tools.companyDetails.enterAnswerPlaceholder')
                            }
                            InputProps={{ maxLength: maxLength }}
                            control={methods.control}
                        />
                    )}
                    {/* No internal submit button, CompanyQuestion handles submission */}
                </Stack>
            </form>
        </FormProvider>
    );
};

export default TextAction;
